// UI strings for Sitkeät sanat, the shared memory images for words that keep failing (Axel's decision of
// 2026-10-09; app/src/memoryimages/): the hint after a failed recall, the image form, the couple session's
// row and page. Finnish (default) and English side by side, the same shape. Style: docs/FINNISH-STYLE.md.
import type { ImageTextError } from '@valyrio/engine/pedagogy/memory-images';
import { defineArea } from '../area';

/** A person's name in the genitive (Roosa → Roosan, Axel → Axelin; docs/FINNISH-STYLE.md "Nimien taivutus"). */
const fiGen = (name: string) => (/[aeiouyäöå]$/i.test(name) ? `${name}n` : `${name}in`);

export const memoryImagesFi = {
  hint: {
    /** The step's heading after a failed recall. */
    kind: 'Sitkeä sana',
    /** Only when the word has no image yet. */
    none: 'Tämä sana on unohtunut monta kertaa.',
    /** Before the partner's image (straw). */
    partner: (name: string) => `${fiGen(name)} muistikuva:`,
    mine: 'Sinun muistikuvasi:',
    make: 'Tehkää muistikuva',
    addOwn: 'Lisää omasi',
    edit: 'Muokkaa',
    remove: 'Poista',
    removeLabel: (name: string) => `Poista ${fiGen(name)} muistikuva`,
    removeMine: 'Poista muistikuvasi',
    next: 'Jatka',
  },
  form: {
    field: 'Muistikuva suomeksi',
    help: 'Suomen sana, joka kuulostaa valyrian sanalta, ja mielikuva, joka yhdistää sen sanan merkitykseen.',
    placeholder: 'esim. vala – miehen vala',
    save: 'Tallenna',
    cancel: 'Peru',
    saved: 'Muistikuva on tallennettu. Se näkyy teille molemmille.',
    error: {
      empty: 'Kirjoita ensin muistikuva.',
      too_long: 'Muistikuva saa olla enintään 200 merkkiä pitkä.',
      hv_form: 'Kirjoita muistikuva suomeksi, ilman kirjaimia ā, ē, ī, ō, ū, ȳ.',
      only_word: 'Pelkkä sana ei ole muistikuva. Lisää siihen suomen sana tai mielikuva.',
    } as Record<ImageTextError, string>,
  },
  row: {
    title: 'Sitkeät sanat',
    waiting: (n: number) => (n === 1 ? '1 sana odottaa muistikuvaa' : `${n} sanaa odottaa muistikuvaa`),
  },
  page: {
    title: 'Sitkeät sanat',
    empty: 'Jokaisella sitkeällä sanalla on nyt muistikuva.',
    /** Before the word (in the HV face). */
    savedWord: 'Muistikuva tallennettu:',
  },
};

export const memoryImagesEn: typeof memoryImagesFi = {
  hint: {
    kind: 'Stubborn word',
    none: 'This word has slipped away many times.',
    partner: (name: string) => `${name}’s memory image:`,
    mine: 'Your memory image:',
    make: 'Make a memory image',
    addOwn: 'Add yours',
    edit: 'Edit',
    remove: 'Remove',
    removeLabel: (name: string) => `Remove ${name}’s memory image`,
    removeMine: 'Remove your memory image',
    next: 'Continue',
  },
  form: {
    field: 'Memory image in Finnish',
    help: 'A Finnish word that sounds like the High Valyrian word, and a picture that ties it to the word’s meaning.',
    placeholder: 'e.g. vala – miehen vala',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'The memory image is saved. You both see it.',
    error: {
      empty: 'Write a memory image first.',
      too_long: 'A memory image may be 200 characters long at most.',
      hv_form: 'Write the memory image in Finnish, without the letters ā, ē, ī, ō, ū, ȳ.',
      only_word: 'The word alone is not a memory image. Add a Finnish word or a picture to it.',
    },
  },
  row: {
    title: 'Stubborn words',
    waiting: (n: number) => (n === 1 ? '1 word waiting for a memory image' : `${n} words waiting for a memory image`),
  },
  page: {
    title: 'Stubborn words',
    empty: 'Every stubborn word has a memory image now.',
    savedWord: 'Memory image saved:',
  },
};

// Messages.memoryImages (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('memoryImages', memoryImagesFi, memoryImagesEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof memoryImagesFi> {}
}
